'use client';

import { BodyDefault } from '../../../../components/typography';
import { Input } from '../../../../components/input';
import { Select } from '../../../../components/select';
import { Switch } from '../../../../components/switch';
import { FieldWrapper } from '../../../../components/field-wrapper/FieldWrapper';
import { ssoProviderOptions } from '../../../application-credentials';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Tick01Icon, ArrowDown01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useAddCredentialsDialogStore } from '../AddCredentialsDialogStore';
import { PasswordField } from '../../../inputs/PasswordField';
import { useShallow } from 'zustand/shallow';

type LoginInformationStepProps = {
  isEditing?: boolean;
};

export const LoginInformationStep = ({
  isEditing,
}: LoginInformationStepProps) => {
  const t = useTranslations(
    'designSystem.credentials.addDialog.steps.loginInformation',
  );
  const {
    credential,
    formMeta,
    setCredential,
    setFormMeta,
    validationErrors,
    setValidationErrors,
  } = useAddCredentialsDialogStore(
    useShallow((state) => ({
      credential: state.credential,
      formMeta: state.formMeta,
      setCredential: state.setCredential,
      setFormMeta: state.setFormMeta,
      validationErrors: state.validationErrors,
      setValidationErrors: state.setValidationErrors,
    })),
  );

  return (
    <VStack alignItems="stretch" gap={4} w="full">
      <FieldWrapper
        label={t('loginUrl.label')}
        helperText={t('loginUrl.helper')}
        error={validationErrors.loginUrl}
      >
        <Input
          size="sm"
          placeholder={t('loginUrl.placeholder')}
          value={credential.loginUrl ?? ''}
          onChange={(e) => {
            setCredential({ loginUrl: e.target.value || null });
            setValidationErrors({ loginUrl: '' });
          }}
        />
      </FieldWrapper>

      <FieldWrapper label={t('basicAuth.label')}>
        <Switch
          size="sm"
          checked={formMeta.requiresBasicAuth}
          onCheckedChange={({ checked }) => {
            setFormMeta({ requiresBasicAuth: checked });
            if (!checked) {
              setCredential({
                basicAuthUsername: '',
                basicAuthPassword: '',
              });
            }
          }}
        >
          <BodyDefault as="span" color="fg.neutral.muted">
            {formMeta.requiresBasicAuth
              ? t('basicAuth.yes')
              : t('basicAuth.no')}
          </BodyDefault>
        </Switch>
      </FieldWrapper>

      {formMeta.requiresBasicAuth && (
        <HStack gap={2} w="full" alignItems="flex-start">
          <Box flex={1}>
            <FieldWrapper
              label={t('basicAuth.username.label')}
              isRequired
              error={validationErrors.basicAuthUsername}
            >
              <Input
                size="sm"
                placeholder={t('basicAuth.username.placeholder')}
                value={credential.basicAuthUsername}
                onChange={(e) => {
                  setCredential({
                    basicAuthUsername: e.target.value,
                  });
                  setValidationErrors({ basicAuthUsername: '' });
                }}
              />
            </FieldWrapper>
          </Box>
          <Box flex={1} pt="5">
            <PasswordField
              label=" "
              placeholder={t('basicAuth.password.placeholder')}
              value={credential.basicAuthPassword}
              onChange={(value) => {
                setCredential({ basicAuthPassword: value });
                setValidationErrors({ basicAuthPassword: '' });
              }}
              error={validationErrors.basicAuthPassword}
              hidePasswordToggle={isEditing}
            />
          </Box>
        </HStack>
      )}

      <FieldWrapper label={t('ssoProvider.label')}>
        <Select.Root
          size="sm"
          positioning={{ sameWidth: true, strategy: 'fixed' }}
          value={[credential.ssoProvider]}
          collection={ssoProviderOptions}
          onValueChange={({ value }) =>
            setCredential({ ssoProvider: value[0] })
          }
        >
          <Select.Control>
            <Select.Trigger>
              <Select.ValueText
                placeholder={t('ssoProvider.placeholder')}
                fontSize="xs"
              />
              <ArrowDown01Icon size={16} />
            </Select.Trigger>
          </Select.Control>
          <Select.Positioner>
            <Select.Content>
              <Select.ItemGroup>
                {ssoProviderOptions.items.map((option) => (
                  <Select.Item key={option.value} item={option}>
                    <Select.ItemText>{option.label}</Select.ItemText>
                    <Select.ItemIndicator>
                      <Tick01Icon size={16} />
                    </Select.ItemIndicator>
                  </Select.Item>
                ))}
              </Select.ItemGroup>
            </Select.Content>
          </Select.Positioner>
        </Select.Root>
      </FieldWrapper>

      {/* TODO: Add failed login attempt limit after the backend will handle that */}
      {/* <Field.Root>
          <HStack gap={1} alignItems="center">
            <Field.Label fontSize="xs">
              {t('failedLoginAttemptLimit.label')}
            </Field.Label>
            <Tooltip
              title={t('failedLoginAttemptLimit.tooltip')}
              positioning={{ placement: 'top' }}
            >
              <CircleHelpIcon size={16} cursor="pointer" />
            </Tooltip>
          </HStack>
          <Input
            size="sm"
            type="number"
            step="1"
            min={0}
            placeholder={t('failedLoginAttemptLimit.placeholder')}
            value={formMeta.failedLoginAttemptLimit}
            onChange={(e) =>
              setFormMeta({ failedLoginAttemptLimit: e.target.value })
            }
          />
        </Field.Root> */}
    </VStack>
  );
};
